import { useState, useEffect } from 'react';
import { Icons } from '../Icons';
import './PINPad.css';

interface PINPadProps {
  length?: number; // 4, 5, or 6 digits
  onComplete: (pin: string) => void;
  onError?: (error: string) => void;
  title?: string;
  subtitle?: string;
  error?: string | null;
  loading?: boolean;
}

export function PINPad({
  length = 6,
  onComplete,
  onError,
  title = 'Entrez votre code PIN',
  subtitle,
  error,
  loading = false,
}: PINPadProps) {
  const [pin, setPin] = useState<string>('');
  const [shake, setShake] = useState(false);

  // Auto-submit when PIN is complete
  useEffect(() => {
    if (pin.length === length && !loading) {
      console.log('🔢 PIN complete, calling onComplete');
      onComplete(pin);
    }
  }, [pin, length, onComplete, loading]);

  // Trigger shake animation on error
  useEffect(() => {
    if (error) {
      setShake(true);
      // Clear PIN on error
      setTimeout(() => {
        setPin('');
        setShake(false);
      }, 500);
    }
  }, [error]);

  const handleNumberClick = (num: number) => {
    if (pin.length < length && !loading) {
      console.log('🔢 Digit entered:', num);
      setPin(prev => prev + num.toString());
    }
  };

  const handleBackspace = () => {
    if (pin.length > 0 && !loading) {
      console.log('🔢 Backspace pressed');
      setPin(prev => prev.slice(0, -1));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (loading) return;

    if (e.key >= '0' && e.key <= '9') {
      handleNumberClick(parseInt(e.key));
    } else if (e.key === 'Backspace') {
      handleBackspace();
    }
  };

  return (
    <div className="pin-pad" onKeyDown={handleKeyPress} tabIndex={0}>
      {/* Title */}
      <div className="pin-header">
        <h2 className="pin-title">{title}</h2>
        {subtitle && <p className="pin-subtitle">{subtitle}</p>}
      </div>

      {/* PIN Dots Display */}
      <div className={`pin-dots ${shake ? 'shake' : ''}`}>
        {Array.from({ length }, (_, i) => (
          <div
            key={i}
            className={`pin-dot ${i < pin.length ? 'filled' : ''}`}
          />
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="pin-error">
          <Icons.AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="pin-loading">
          <Icons.Loader size={20} className="spinner" />
          <span>Vérification...</span>
        </div>
      )}

      {/* Numeric Keypad */}
      <div className="pin-keypad">
        {/* Rows 1-3 */}
        {[
          [1, 2, 3],
          [4, 5, 6],
          [7, 8, 9],
        ].map((row, rowIndex) => (
          <div key={rowIndex} className="pin-row">
            {row.map(num => (
              <button
                key={num}
                className="pin-key"
                onClick={() => handleNumberClick(num)}
                disabled={loading || pin.length >= length}
              >
                {num}
              </button>
            ))}
          </div>
        ))}

        {/* Bottom row: Empty, 0, Backspace */}
        <div className="pin-row">
          <div className="pin-key empty" /> {/* Empty space */}
          <button
            className="pin-key"
            onClick={() => handleNumberClick(0)}
            disabled={loading || pin.length >= length}
          >
            0
          </button>
          <button
            className="pin-key backspace"
            onClick={handleBackspace}
            disabled={loading || pin.length === 0}
          >
            <Icons.Delete size={24} />
          </button>
        </div>
      </div>

      {/* Hint */}
      <p className="pin-hint">
        Code PIN de {length} chiffres
      </p>
    </div>
  );
}
